import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { TournamentProvider } from "@/lib/TournamentContext";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import TopNav from "@/components/app/TopNav";
import CompetitionLayout from "@/components/app/CompetitionLayout";
import InstallPrompt from "@/components/app/InstallPrompt";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Predict from "@/pages/Predict";
import Fixtures from "@/pages/Fixtures";
import Survivor from "@/pages/Survivor";
import Bracket from "@/pages/Bracket";
import BracketKO from "@/pages/BracketKO";
import Fantasy from "@/pages/Fantasy";
import Bonus from "@/pages/Bonus";
import Leaderboard from "@/pages/Leaderboard";
import GroupProfile from "@/pages/GroupProfile";
import GroupManage from "@/pages/GroupManage";
import JoinGroup from "@/pages/JoinGroup";
import Profile from "@/pages/Profile";
import Standings from "@/pages/Standings";
import Chat from "@/pages/Chat";
import Admin from "@/pages/Admin";
import Rules from "@/pages/Rules";

function AppShell() {
  return (
    <>
      <TopNav />
      <Outlet />
      <InstallPrompt />
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: check session_id synchronously to prevent race conditions
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth-callback" element={<AuthCallback />} />

      <Route element={
        <ProtectedRoute>
          <TournamentProvider>
            <AppShell />
          </TournamentProvider>
        </ProtectedRoute>
      }>
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/fixtures" element={<Fixtures />} />
        <Route element={<CompetitionLayout />}>
          <Route path="/predict" element={<Predict />} />
          <Route path="/survivor" element={<Survivor />} />
          <Route path="/bracket" element={<Bracket />} />
          <Route path="/bracket-ko" element={<BracketKO />} />
          <Route path="/fantasy" element={<Fantasy />} />
          <Route path="/bonus" element={<Bonus />} />
        </Route>
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/standings" element={<Standings />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/groups" element={<Navigate to="/leaderboard" replace />} />
        <Route path="/groups/:groupId" element={<GroupProfile />} />
        <Route path="/groups/:groupId/manage" element={<GroupManage />} />
        <Route path="/join/:code" element={<JoinGroup />} />
        <Route path="/join/:code/:slug" element={<JoinGroup />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/rules" element={<Rules />} />
      </Route>

      <Route element={
        <ProtectedRoute adminOnly>
          <TournamentProvider>
            <AppShell />
          </TournamentProvider>
        </ProtectedRoute>
      }>
        <Route path="/admin" element={<Admin />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            classNames: {
              toast: "!bg-zinc-950 !border !border-zinc-800 !text-white !rounded-none",
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
